import { useState } from "react";

interface ConsoleLogoProps {
  logo?: string;
  emoji: string;
  name: string;
  /** "light" plate for dark/colored wordmarks (default); "dark" for white wordmarks. */
  plate?: "light" | "dark";
  /** Rendered logo height inside the plate. Defaults to "1.7rem". */
  height?: string;
}

/**
 * Renders a console wordmark in its ORIGINAL colors on a rounded "label" plate
 * so it stays legible on the dark CRT tiles. Light plate for dark/colored
 * wordmarks (the default); dark plate for predominantly-white wordmarks
 * (Genesis, Master System). Falls back to the emoji glyph if the logo is
 * missing or the image fails to load.
 */
export function ConsoleLogo({
  logo,
  emoji,
  name,
  plate = "light",
  height = "1.7rem",
}: ConsoleLogoProps) {
  const [failed, setFailed] = useState(false);

  if (logo && !failed) {
    const isDark = plate === "dark";
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0.3rem 0.55rem",
          borderRadius: "0.5rem",
          maxWidth: "100%",
          background: isDark ? "#101218" : "#f4f4f5",
          border: isDark
            ? "1px solid rgba(255,255,255,0.16)"
            : "1px solid rgba(0,0,0,0.1)",
          boxShadow: isDark
            ? "0 1px 2px rgba(0,0,0,0.5)"
            : "0 1px 3px rgba(0,0,0,0.45)",
        }}
      >
        <img
          src={logo}
          alt={`${name} logo`}
          loading="lazy"
          onError={() => setFailed(true)}
          style={{
            height,
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            display: "block",
          }}
        />
      </span>
    );
  }

  return (
    <span aria-hidden="true" style={{ fontSize: "2rem", lineHeight: 1 }}>
      {emoji}
    </span>
  );
}
